import { useEffect, useState, useRef } from 'react';
import { FileText, Download, Trash2, Replace, Plus, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  getProjectDocuments,
  uploadDocument,
  deleteDocument,
  getDocumentDownloadUrl,
  inferDocumentType,
} from '@/lib/database';
import { useReplaceDocument, ACCEPTED_DOCUMENT_TYPES as ACCEPTED_TYPES } from '@/hooks/useReplaceDocument';
import type { Tables } from '@/types/database.types';
import { format } from 'date-fns';

type DocumentRow = Tables<'documents'>;

const documentTypeLabels: Record<string, string> = {
  meeting_notes: 'Meeting Notes',
  email_thread: 'Email Thread',
  slack_export: 'Slack Export',
  project_plan: 'Project Plan',
  status_report: 'Status Report',
  other: 'Other',
};

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ProjectDocumentsSectionProps {
  projectId: string;
  isReadOnly: boolean;
  /** Triggers the Regenerate-from-documents flow. When provided, a
   *  "Regenerate" button renders next to Add so users can re-run AI
   *  extraction on the current document set without leaving this tab. */
  onRegenerateClick?: () => void;
}

// Temporarily hidden pending a fix for issues found in testing (see Linear
// ticket). Flip to true — or remove this guard — to restore the button.
const SHOW_REGENERATE = false;

export function ProjectDocumentsSection({ projectId, isReadOnly, onRegenerateClick }: ProjectDocumentsSectionProps) {
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Replace-file interaction is shared with the workspace documents page.
  const {
    replacingId,
    inputRef: replaceInputRef,
    startReplace: handleReplaceClick,
    onFileSelected: handleReplaceFile,
  } = useReplaceDocument((updated) =>
    setDocuments((prev) => prev.map((d) => (d.id === updated.id ? updated : d)))
  );

  useEffect(() => {
    loadDocuments();
  }, [projectId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await getProjectDocuments(projectId);
      setDocuments(data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const docType = inferDocumentType(file.name);
        const newDoc = await uploadDocument(projectId, file, docType);
        setDocuments(prev => [newDoc, ...prev]);
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (doc: DocumentRow) => {
    try {
      setDownloadingId(doc.id);
      const url = await getDocumentDownloadUrl(doc.file_path);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to download document:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (doc: DocumentRow) => {
    if (!confirm(`Delete "${doc.original_name}"? This cannot be undone.`)) return;
    try {
      setDeletingId(doc.id);
      await deleteDocument(doc.id);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Failed to delete document:', error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-ingesta-blue-500" />
            Documents
          </CardTitle>
          {!isReadOnly && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-1" />
                )}
                Add
              </Button>
              {SHOW_REGENERATE && onRegenerateClick && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRegenerateClick}
                  disabled={uploading}
                  title="Re-run AI extraction on the current document set"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Regenerate
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_TYPES}
                onChange={handleUpload}
                className="hidden"
              />
              <input
                ref={replaceInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                onChange={handleReplaceFile}
                className="hidden"
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            No documents yet.{!isReadOnly && ' Add documents to keep project files organized.'}
          </p>
        ) : (
          <div className="divide-y">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center gap-3 py-3 group">
                <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {doc.original_name}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                      {documentTypeLabels[doc.type] || doc.type}
                    </span>
                    <span>{formatFileSize(doc.file_size)}</span>
                    {doc.uploaded_at && (
                      <span>{format(new Date(doc.uploaded_at), 'MMM dd, yyyy')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDownload(doc)}
                    disabled={downloadingId === doc.id}
                    title="Download"
                  >
                    {downloadingId === doc.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                  {!isReadOnly && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleReplaceClick(doc.id)}
                        disabled={replacingId === doc.id}
                        title="Replace"
                      >
                        {replacingId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Replace className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDelete(doc)}
                        disabled={deletingId === doc.id}
                        title="Delete"
                      >
                        {deletingId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
